import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Star, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { LocalBusinessSchema, GeoTargetingSchema, BreadcrumbSchema, FAQSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { services, testimonials, businessInfo } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const MidlandPage = () => {
  const midlandFaqs = [
    {
      question: 'Do you provide plumbing services in Midland, TX?',
      answer: 'Yes! Basin Plumbing proudly serves all of Midland, TX including Downtown, Grassland Estates, Midkiff, Greenwood, Fannin Terrace, and surrounding neighborhoods. We offer the same great services in Midland as we do in our home base of Odessa.'
    },
    {
      question: 'How quickly can you get to Midland for an emergency?',
      answer: 'For plumbing emergencies in Midland, we typically arrive within 30-45 minutes. Our 24/7 emergency service ensures you\'re never left waiting, even on nights, weekends, and holidays.'
    },
    {
      question: 'What services do you offer in Midland?',
      answer: 'We offer all our services in Midland including water heater installation and repair, drain cleaning, gas line repair and installation, sewer line replacement, trenchless plumbing, medical gas installation, and complete residential plumbing.'
    },
    {
      question: 'Do you handle Midland\'s hard water issues?',
      answer: 'Absolutely. Midland has some of the hardest water in Texas, which causes scale buildup, water heater problems, and pipe corrosion. We specialize in water heater maintenance, tankless systems, and solutions that address hard water damage.'
    },
    {
      question: 'What areas of Midland do you serve?',
      answer: 'We serve all Midland neighborhoods including Downtown Midland, Grassland Estates, Midkiff, Greenwood, Fannin Terrace, Midland Park Mall area, Tall City, and everywhere in between. We also serve nearby communities like Stanton and Greenwood ISD areas.'
    },
    {
      question: 'Are your plumbers licensed to work in Midland?',
      answer: 'Yes, all Basin Plumbing technicians are fully licensed Texas plumbers, insured, and familiar with Midland building codes and permit requirements. We handle permits for you on all applicable jobs.'
    }
  ];

  const midlandNeighborhoods = [
    'Downtown Midland',
    'Grassland Estates', 
    'Midkiff',
    'Greenwood',
    'Fannin Terrace',
    'Tall City',
    'Midland Park Mall Area',
    'Fasken',
    'Saddle Club',
    'Ranchland Hills'
  ];

  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Midland Plumbers', url: 'https://basinplumbing.com/plumbers-in-midland/' }
  ];

  return (
    <>
      <Seo {...seoConfig.midland} />
      <LocalBusinessSchema />
      <GeoTargetingSchema city="Midland" />
      <BreadcrumbSchema items={breadcrumbs} />
      <FAQSchema customFaqs={midlandFaqs} />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
          
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Midland Plumbers</span>
            </nav>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                  <MapPin size={16} className="text-[#e85a24]" />
                  <span className="text-sm font-medium">Serving Midland, TX & Surrounding Areas</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  Professional Plumbers in{' '}
                  <span className="text-[#e85a24]">Midland, Texas</span>
                </h1>
                
                <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                  Basin Plumbing provides expert residential and commercial plumbing services 
                  to Midland, TX. Available 24/7 for all your plumbing emergencies.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${businessInfo.phone}`}>
                    <Button 
                      size="lg" 
                      className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-8 py-6 text-lg w-full sm:w-auto"
                    >
                      <Phone className="mr-2" size={20} />
                      {businessInfo.phone}
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-white text-white hover:bg-white hover:text-[#1a3a5c] px-8 py-6 text-lg w-full sm:w-auto"
                    >
                      Get Free Estimate
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center gap-6 mt-8">
                  <div className="flex -space-x-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-10 h-10 bg-gray-300 rounded-full border-2 border-white"></div>
                    ))}
                  </div>
                  <div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-sm text-blue-200">Trusted by 100+ Midland residents</p>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                <img 
                  src="/images/plumber-kitchen-sink.jpg" 
                  alt="Plumber serving Midland TX"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us for Midland */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-4">
                Why Midland Chooses Basin Plumbing
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Fast, reliable plumbing services tailored for Midland homes and businesses
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: '30-45 Minute Response',
                  desc: 'We arrive in Midland fast for emergencies — day or night, weekends and holidays included'
                },
                {
                  icon: Star,
                  title: 'Midland Hard Water Experts',
                  desc: 'We understand local water quality issues and specialize in solutions that last'
                },
                {
                  icon: CheckCircle,
                  title: 'Satisfaction Guaranteed',
                  desc: 'We stand behind our work with comprehensive warranties on parts and labor'
                }
              ].map((item, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-[#e85a24] rounded-xl flex items-center justify-center mx-auto mb-4">
                      <item.icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1a3a5c] mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Midland-Specific Challenges Section */}
        <section className="py-16 bg-[#1a3a5c] text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  We Know Midland Plumbing Challenges
                </h2>
                <p className="text-blue-100 mb-8 leading-relaxed">
                  Midland's unique conditions create specific plumbing challenges that require 
                  local expertise. From extremely hard water to the demands of the oil and gas 
                  industry, we've seen it all and know how to fix it right the first time.
                </p>
                
                <div className="space-y-4">
                  {[
                    {
                      title: 'Hard Water Scale Buildup',
                      desc: 'Midland water is among the hardest in Texas. We specialize in descaling, water heater maintenance, and tankless systems that handle mineral-heavy water.'
                    },
                    {
                      title: 'Aging Infrastructure',
                      desc: 'Many Midland homes have older plumbing that needs updating. Our trenchless solutions minimize disruption while modernizing your system.'
                    },
                    {
                      title: 'Commercial & Oilfield Support',
                      desc: 'We serve Midland businesses and support facilities with commercial plumbing, backflow prevention, and medical gas installation.'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 bg-white/10 rounded-xl p-4">
                      <CheckCircle className="text-[#e85a24] flex-shrink-0 mt-1" size={20} />
                      <div>
                        <h4 className="font-semibold mb-1">{item.title}</h4>
                        <p className="text-blue-200 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="hidden lg:block">
                <img 
                  src="/images/plumber-hydro-jetting.jpg" 
                  alt="Basin Plumbing technician performing hydro jetting service in Midland TX"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services for Midland */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-4">
                Plumbing Services in Midland, TX
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Complete plumbing solutions for your Midland property
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.slice(0, 8).map((service) => (
                <Link key={service.id} to={`/services/${service.slug}`}>
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2 group-hover:text-[#e85a24] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {service.shortDesc}
                      </p>
                      <span className="text-[#e85a24] font-medium text-sm flex items-center gap-1">
                        Learn More
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-4">
                What Our Midland Customers Say
              </h2>
              <p className="text-gray-600">Real reviews from Midland homeowners we've helped</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.filter(t => t.location.includes('Midland') || t.location.includes('Odessa')).slice(0, 3).map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={18} className="text-yellow-400" fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                    <div>
                      <p className="font-semibold text-[#1a3a5c]">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Midland Neighborhoods Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-4">
                Serving All Midland Neighborhoods
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                From Downtown to Greenwood, we provide fast plumbing service throughout Midland and surrounding areas.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {midlandNeighborhoods.map((neighborhood) => (
                <span 
                  key={neighborhood}
                  className="flex items-center gap-2 px-5 py-2 bg-white border border-gray-200 rounded-full text-gray-700 font-medium hover:border-[#e85a24] hover:bg-[#e85a24]/5 transition-colors"
                >
                  <MapPin size={14} className="text-[#e85a24]" />
                  {neighborhood}
                </span>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-3xl font-bold text-[#e85a24]">30-45 min</p>
                  <p className="text-gray-600">Emergency Response to Midland</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#1a3a5c]">100+</p>
                  <p className="text-gray-600">Midland Families Served</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#e85a24]">24/7</p>
                  <p className="text-gray-600">Always Available</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-4">
                Midland Plumbing FAQs
              </h2>
              <p className="text-gray-600">Common questions about our Midland plumbing services</p>
            </div>

            <div className="space-y-4">
              {midlandFaqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                      <span className="font-semibold text-[#1a3a5c] pr-4">{faq.question}</span>
                      <ArrowRight className="text-[#e85a24] transform group-open:rotate-90 transition-transform flex-shrink-0" size={20} />
                    </summary>
                    <div className="px-6 pb-4 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Need a Plumber in Midland?"
          subtitle="Contact Basin Plumbing today for fast, reliable service in Midland, TX."
        />
      </main>

      <Footer />
    </>
  );
};

export default MidlandPage;
